/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation, useQuery } from 'react-query'

import { deleteCluster, getCluster } from '@modules/ccm-admin-api/v1/callers'
import type {
  Cluster,
  ClusterId,
  ClusterList,
  MultiErrorResponse,
  operations,
} from '@modules/ccm-admin-api/v1/types'
import { queryClient } from '@modules/query'

import { exportClusterList, listClustersWithHeaders } from './callers_extra'
import { processCSVPages } from './csv_utils'

import type { UseMutationOptions, UseQueryOptions } from 'react-query'

const CCM_LIST_CLUSTER_QUERY_KEY = 'adminCcmClusterList'

const CCM_CLUSTER_QUERY_KEY = 'adminCcmCluster'

type AdminListClustersQueryParameters = operations['adminListClusters']['parameters']['query']
type AdminListClustersHeaderParameters = operations['adminListClusters']['parameters']['header']

export const useGetCCMClusters = (args?: {
  queryParameters?: AdminListClustersQueryParameters
  headerParameters?: AdminListClustersHeaderParameters
  options?: Omit<UseQueryOptions<ClusterList, MultiErrorResponse>, 'queryKey' | 'queryFn'>
}) =>
  useQuery<ClusterList, MultiErrorResponse>(
    [CCM_LIST_CLUSTER_QUERY_KEY, args?.queryParameters, args?.headerParameters],
    () => listClustersWithHeaders(args),
    args?.options,
  )

export const useDeleteCCMCluster = (
  options?: Omit<
    UseMutationOptions<
      void,
      MultiErrorResponse,
      {
        id: ClusterId
      }
    >,
    'mutationFn'
  >,
) =>
  useMutation<
    void,
    MultiErrorResponse,
    {
      id: ClusterId
    }
  >(
    ({ id }) =>
      deleteCluster({
        pathParameters: {
          id,
        },
      }),
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries(CCM_LIST_CLUSTER_QUERY_KEY)

        const [, { id }] = args

        queryClient.invalidateQueries([CCM_CLUSTER_QUERY_KEY, id])

        options?.onSuccess?.(...args)
      },
    },
  )

// Export full CSV across all pages using header-based pagination
export const useExportCCMClusterList = () =>
  useMutation<string, MultiErrorResponse, AdminListClustersQueryParameters>(
    async (queryParameters) => {
      const asyncIterator = exportClusterList(queryParameters)

      return processCSVPages(asyncIterator)
    },
  )

export const useGetCCMCluster = (
  clusterId: string,
  options?: Omit<UseQueryOptions<Cluster, MultiErrorResponse>, 'queryKey' | 'queryFn'>,
) =>
  useQuery<Cluster, MultiErrorResponse>(
    [CCM_CLUSTER_QUERY_KEY, clusterId],
    () =>
      getCluster({
        pathParameters: {
          id: clusterId,
        },
      }),
    options,
  )
